import axios from "axios";
import { OpenAPI } from "./core/OpenAPI";
import { getMockResponse } from "./mockResponses";
import { getCsrfToken } from "../utils/csrf";

// OpenAPI.BASEを設定
const ROOM_DESIGNS_PATH = "/api/room-designs";

export const ItemRemovalService = {
  // 家具除去処理を実行するAPI
  processItemRemoval: async (params: any) => {
    const { formData } = params;
    const form = new FormData();

    for (const key in formData) {
      form.append(key, formData[key]);
    }

    // モックモードが有効な場合
    if (OpenAPI.USE_MOCK) {
      // 少し遅延を入れてAPIリクエストをシミュレート
      return new Promise((resolve) => {
        setTimeout(() => {
          // モックレスポンスを取得
          const mockResponse = getMockResponse(
            "/item-removal/process",
            "POST",
            formData,
          );
          // スタブIDを追加
          mockResponse.stub_id = formData.stub_id;
          resolve(mockResponse);
        }, 800);
      });
    }

    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/item-removal/process/`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      return { message: "ネットワークエラーが発生しました" };
    }
  },

  // 処理済み画像をクリアするAPI
  clearProcessedImages: async (id: number) => {
    // モックモードが有効な場合
    if (OpenAPI.USE_MOCK) {
      // 少し遅延を入れてAPIリクエストをシミュレート
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(getMockResponse("/item-removal/clear/", "POST", { id }));
        }, 300);
      });
    }

    try {
      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/item-removal/clear/${id}/`,
        {},
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { message: "ネットワークエラーが発生しました" };
    }
  },

  // 処理済み画像を再実行するAPI（全体モード用）
  // POST /item-removal/regen/{id}/
  regenerateProcessedImage: async (id: number) => {
    // モックモードが有効な場合
    if (OpenAPI.USE_MOCK) {
      // 少し遅延を入れてAPIリクエストをシミュレート
      return new Promise((resolve) => {
        setTimeout(() => {
          const mockResponse = getMockResponse("/item-removal/regen/", "POST", {
            id,
          });
          // IDを設定
          mockResponse.id = id;
          resolve(mockResponse);
        }, 500);
      });
    }

    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/item-removal/regen/${id}/`,
        {},
        {
          headers: {
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { message: "ネットワークエラーが発生しました" };
    }
  },

  // マスク指定で処理済み画像を再実行するAPI（一部モード用）
  // POST /item-removal/masked/regen/{id}/
  regenerateMaskedProcessedImage: async (id: number, maskImage: Blob) => {
    // モックモードが有効な場合
    if (OpenAPI.USE_MOCK) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const mockResponse = getMockResponse("/item-removal/masked/regen/", "POST", {
            id,
          });
          mockResponse.id = id;
          resolve(mockResponse);
        }, 500);
      });
    }

    try {
      const csrfToken = getCsrfToken();
      const form = new FormData();
      const timestamp = Date.now();
      form.append('mask_image', maskImage, `mask_${timestamp}.png`);

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/item-removal/masked/regen/${id}/`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { success: false, message: "ネットワークエラーが発生しました" };
    }
  },

  // 家具画像を追加するAPI
  addItemImages: async (params: any) => {
    const { formData } = params;
    const form = new FormData();

    for (const key in formData) {
      form.append(key, formData[key]);
    }

    // モックモードが有効な場合
    if (OpenAPI.USE_MOCK) {
      // 少し遅延を入れてAPIリクエストをシミュレート
      return new Promise((resolve) => {
        setTimeout(() => {
          const mockResponse = getMockResponse(
            "/item-removal/add-images/",
            "POST",
            formData,
          );
          // スタブIDを追加
          mockResponse.stub_id = formData.stub_id;
          resolve(mockResponse);
        }, 600);
      });
    }

    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/item-removal/add-images/`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { message: "ネットワークエラーが発生しました" };
    }
  },

  // 互換性のために古いメソッド名も残す
  clearAfterImages: function (id: number) {
    return this.clearProcessedImages(id);
  },

  regenAfterImages: function (id: number) {
    return this.regenerateProcessedImage(id);
  },

  regenMaskedAfterImages: function (id: number, maskImage: Blob) {
    return this.regenerateMaskedProcessedImage(id, maskImage);
  },

  // マスク指定家具消し処理を実行するAPI
  processMaskedItemRemoval: async (params: {
    formData: {
      stub_id: string;
      item_image: File;
      mask_image: Blob;
    }
  }) => {
    const { formData } = params;
    const form = new FormData();
    const timestamp = Date.now();
    form.append('stub_id', formData.stub_id);
    form.append('item_image', formData.item_image);
    form.append('mask_image', formData.mask_image, `mask_${timestamp}.png`);

    // モックモードが有効な場合
    if (OpenAPI.USE_MOCK) {
      return new Promise((resolve) => {
        setTimeout(() => {
          const mockResponse = getMockResponse(
            "/item-removal/masked/",
            "POST",
            formData,
          );
          mockResponse.stub_id = formData.stub_id;
          resolve(mockResponse);
        }, 800);
      });
    }

    try {
      const csrfToken = getCsrfToken();

      const response = await axios.post(
        `${OpenAPI.BASE}${ROOM_DESIGNS_PATH}/item-removal/masked/`,
        form,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-CSRFToken": csrfToken,
          },
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return error.response.data;
      }
      return { success: false, message: "ネットワークエラーが発生しました" };
    }
  },
};
